<template>
  <div class="content-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8" class="text-center">
            <h1 class="hero-title mb-2">교육 과정 및 교재 샘플</h1>
            <div class="hero-subtitle mb-2">Curriculum & Resources</div>
            <p class="hero-description">체계적인 교육 시스템과 다양한 교재로 효과적인 영어 학습을 지원합니다</p>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Info Section -->
    <section class="content-info-section">
      <v-container>
        <v-row justify="center">
          <v-col cols="12">
            <v-card class="info-card mb-6" rounded="lg" elevation="3">
              <div class="card-content">
                <div class="feature-grid">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-book-open-page-variant</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">맞춤형 교재 제공</h4>
                            <p class="feature-desc">학습자의 수준과 목표에 최적화된 교재 제공</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-stairs-up</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">단계별 학습 시스템</h4>
                            <p class="feature-desc">초급부터 고급까지 체계적인 학습 경로 설계</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-file-document-multiple</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">다양한 주제별 교재</h4>
                            <p class="feature-desc">일상회화, 비즈니스, 시험대비 등 목적별 교재</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-check-circle</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">품질 보증</h4>
                            <p class="feature-desc">엄선된 고품질 교재로 효과적인 학습 경험 제공</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-school</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">커리큘럼 맞춤화</h4>
                            <p class="feature-desc">학습자의 목표와 필요에 따른 맞춤형 학습 설계</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="feature-item">
                        <div class="card-title-container">
                          <div class="icon-wrapper">
                            <v-icon color="primary" size="20">mdi-account-group</v-icon>
                          </div>
                          <div class="title-content">
                            <h4 class="feature-title">모든 연령대 지원</h4>
                            <p class="feature-desc">어린이부터 성인까지 모든 연령대에 맞는 교재</p>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Content Books Section -->
    <section class="books-section">
      <v-container>
        <v-row>
          <v-col cols="12" class="mb-4">
            <h2 class="section-title text-center">교재 샘플</h2>
            <p class="section-subtitle text-center">아래 교재 샘플을 통해 실제 수업에서 사용되는 교재의 품질을 확인하실 수 있습니다</p>
          </v-col>
        </v-row>
        
        <v-row>
          <v-col v-for="t in contentState.content" :key="t.contentId"
                 cols="12" sm="6" md="4" class="mb-5">
            <v-card class="content-card" rounded="lg" elevation="2" height="100%">
              <div class="content-card-inner">
                <div class="content-header">
                  <div class="d-flex align-center">
                    <div class="icon-wrapper-small mr-3">
                      <v-icon color="primary" size="20">mdi-bookshelf</v-icon>
                    </div>
                    <h3 class="content-title">
                      {{ t.description.split('(')[0] }}
                      <span class="text-subtitle-2">({{ t.description.split('(')[1] }}</span>
                    </h3>
                  </div>
                  <v-divider class="my-3"></v-divider>
                  <div class="content-description">
                    <Markdown :markdown="t.contentDesc"/>
                  </div>
                </div>
                
                <v-carousel
                  class="content-carousel"
                  height="320"
                  hide-delimiters
                  :touchless="true"
                  interval="4000"
                  show-arrows
                >
                  <v-carousel-item v-for="(book, i) in t.bookList" :key="i"
                                  reverse-transition="fade-transition"
                                  transition="fade-transition"
                  >
                    <div class="d-flex flex-column align-center">
                      <v-img
                          width="190px"
                          max-height="240px"
                          :src="book.bookLink"
                          class="book-cover my-3"
                      />
                      <v-btn 
                        class="sample-btn mt-3" 
                        elevation="2"
                        color="primary"
                        @click="openWindow(book.bookSampleLink, book.bookName)"
                        rounded
                      >
                        <v-icon left small>mdi-book-open-variant</v-icon>
                        {{ book.bookName }} Sample
                      </v-btn>
                    </div>
                  </v-carousel-item>
                </v-carousel>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script>
import Markdown from '@/components/Markdown'
import {mapActions, mapGetters} from "vuex";

export default {
  name: 'Course',
  data(){
    return {
      book: {},
      content: '### 체계적인 교육 커리큘럼\n\n' +
               '- **맞춤형 교재 제공** - 학습자의 수준과 목표에 최적화된 교재 제공\n' +
               '- **단계별 학습 시스템** - 초급부터 고급까지 체계적인 학습 경로 설계\n' +
               '- **다양한 주제별 교재** - 일상회화, 비즈니스, 시험대비 등 목적별 교재\n\n' +
               '※ 아래 교재 샘플을 통해 실제 수업에서 사용되는 교재의 품질을 확인하실 수 있습니다. 수업에서는 학습자의 수준과 목표에 맞는 더 다양한 교재가 활용됩니다.',
    }
  },
  computed: {
    ...mapGetters(['contentState']),
  },
  components:{
    Markdown
  },
  mounted() {
    this.fetchContent()
    this.fetchCover()
  },
  methods: {
    ...mapActions(['fetchContent', 'fetchCover']),
    openWindow(link, title){
      this.$openWindow(link, title )
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/main.scss";
@import "@/assets/scss/elegant-theme.scss";
@import "@/assets/scss/info-content.scss";

.content-page {
  background-color: #f9f9f9;
  min-height: 100vh;
}

.hero-section {
  padding: 50px 0 30px;

  
  .hero-title {
    font-size: 2.2rem;
    font-weight: 700;
    color: #334155;
    margin-bottom: 0.5rem;
    letter-spacing: -0.5px;
  }
  
  .hero-subtitle {
    font-size: 1.2rem;
    color: #3b82f6;
    font-weight: 600;
  }
  
  .hero-description {
    font-size: 1rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
  }
}

.content-info-section {
  padding: 10px 0 20px;
  margin-top: -15px;
  
  .info-card {
    overflow: hidden;
    transition: all 0.3s ease;
    
    .card-content {
      padding: 20px 15px 15px;
    }
    
    .feature-grid {
      width: 100%;
    }
    
    .feature-item {
      text-align: left;
      padding: 5px;
      height: 100%;
      max-height: 90px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    
    .card-title-container {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
    }
    
    .icon-wrapper {
      background-color: rgba(59, 130, 246, 0.1);
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      flex-shrink: 0;
    }
    
    .title-content {
      flex: 1;
    }
    
    .feature-title {
      font-size: 0.9rem;
      font-weight: 600;
      color: #1e40af;
      margin-bottom: 3px;
      white-space: normal;
    }
    
    .feature-desc {
      color: #64748b;
      font-size: 0.8rem;
      line-height: 1.25;
      margin-bottom: 0;
    }
  }
}

.books-section {
  padding: 20px 0 60px;
  
  .section-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #334155;
    margin-bottom: 8px;
  }
  
  .section-subtitle {
    color: #64748b;
    font-size: 1rem;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .content-card {
    height: 100%;
    transition: all 0.3s ease;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1) !important;
    }
    
    .content-card-inner {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    
    .content-header {
      padding: 20px 20px 10px;
    }
    
    .icon-wrapper-small {
      background-color: rgba(59, 130, 246, 0.1);
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    
    .content-title {
      font-size: 1.15rem;
      font-weight: 600;
      color: #1e40af;
      margin-bottom: 0;
    }
    
    .content-description {
      max-height: 100px;
      overflow-y: visible;
      font-size: 0.9rem;
      color: #64748b;
      margin-bottom: 10px;
      
      &::-webkit-scrollbar {
        width: 4px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 10px;
      }
      
      &::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
      }
      
      :deep(p) {
        margin-bottom: 0.5rem;
      }
      
      :deep(ul) {
        padding-left: 1.2rem;
        margin-bottom: 0.5rem;
      }
    }
    
    .content-carousel {
      flex-grow: 1;
      background-color: rgba(59, 130, 246, 0.03);
    }
    
    .book-cover {
      border-radius: 4px;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
      transition: transform 0.3s ease;
      
      &:hover {
        transform: scale(1.03);
      }
    }
  }
}

.sample-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
  font-size: 0.9rem !important;
  padding: 0 16px !important;
  height: 36px !important;
  
  .v-icon {
    margin-right: 6px;
  }
}

// Dark mode support
.v-application.theme--dark {
  .content-page {
    background-color: #121212;
  }
  
  .hero-section {
    background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
    
    .hero-title {
      color: #e2e8f0;
    }
    
    .hero-subtitle {
      color: #60a5fa;
    }
    
    .hero-description {
      color: #a0aec0;
    }
  }
  
  .content-info-section {
    .info-card {
      background-color: #1e1e1e !important;
      
      .feature-title {
        color: #60a5fa;
      }
      
      .feature-desc {
        color: #a0aec0;
      }
    }
  }
  
  .books-section {
    .section-title {
      color: #e2e8f0;
    }
    
    .section-subtitle {
      color: #a0aec0;
    }
    
    .content-card {
      background-color: #1e1e1e !important;
      
      .content-title {
        color: #60a5fa;
      }
      
      .content-description {
        color: #a0aec0;
        
        &::-webkit-scrollbar-track {
          background: #2d2d2d;
        }
        
        &::-webkit-scrollbar-thumb {
          background: #555;
        }
        
        &::-webkit-scrollbar-thumb:hover {
          background: #666;
        }
      }
      
      .content-carousel {
        background-color: rgba(30, 41, 59, 0.4);
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 960px) {
  .hero-section {
    padding: 35px 0 25px;
    
    .hero-title {
      font-size: 1.8rem;
    }
    
    .hero-subtitle {
      font-size: 1rem;
    }
    
    .hero-description {
      font-size: 0.9rem;
      padding: 0 15px;
    }
  }
  
  .content-info-section {
    padding: 10px 0 20px;
    
    .feature-grid {
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
    
    .card-content {
      padding: 20px 10px !important;
    }
    
    .feature-item {
      padding: 5px !important;
    }
    
    .icon-wrapper {
      width: 45px !important;
      height: 45px !important;
      margin-bottom: 8px !important;
    }
    
    .feature-title {
      font-size: 0.9rem !important;
    }
    
    .feature-desc {
      font-size: 0.8rem !important;
      line-height: 1.3 !important;
    }
  }
  
  .books-section {
    padding: 15px 0 40px;
    
    .section-title {
      font-size: 1.5rem;
    }
    
    .section-subtitle {
      font-size: 0.9rem;
      padding: 0 15px;
    }
    
    .content-card {
      .content-header {
        padding: 15px 15px 5px;
      }
      
      .content-title {
        font-size: 1rem;
      }
      
      .content-description {
        max-height: 80px;
        font-size: 0.85rem;
      }
      
      .content-carousel {
        height: 300px !important;
      }
    }
    
    .sample-btn {
      font-size: 0.85rem !important;
      padding: 0 12px !important;
      height: 32px !important;
    }
  }
}

@media (max-width: 600px) {
  .content-info-section {
    .info-card {
      margin-bottom: 15px !important;
    }
    
    .feature-grid {
      grid-template-columns: 1fr 1fr;
    }
    
    .feature-item {
      .icon-wrapper {
        width: 40px !important;
        height: 40px !important;
      }
      
      .feature-title {
        font-size: 0.85rem !important;
      }
      
      .feature-desc {
        font-size: 0.75rem !important;
      }
    }
  }
  
  .books-section {
    .content-card {
      .content-carousel {
        height: 270px !important;
      }
      
      .book-cover {
        width: 160px !important;
        max-height: 200px !important;
      }
    }
  }
}
</style>